import { contactUnlinkingDialogErrorDescription } from './personaLinkingActions.locstring.json';
import { cancelLabel } from 'owa-locstrings/lib/strings/cancellabel.locstring.json';
import { somethingWentWrongLabel } from 'owa-locstrings/lib/strings/somethingwentwronglabel.locstring.json';
import loc from 'owa-localize';
import type { Dispatch } from 'redux';
import {
    createActionFactory,
    createDataActionFactory,
    HubPerson,
    Person,
    LinkedContact,
    addNotification,
    Feature,
    getLogger,
    createThunk,
    getMatchingFavoritePerson,
    PersonType,
    ThunkDispatch,
    getLogSafeId,
} from '../../../shared';
import { unfavoriteMultiplePersonas } from '../data/favoriteAddRemoveDataActions';
import { addFavoritePersonaClicked } from '../user/favoritingUserActions';
import { getAppState, invalidateCachedContacts, getFavorites } from '../../../appstate';
import {
    unlinkContact as unlinkContactOperation,
    linkPersonas as linkPersonasOperation,
} from '../../dataLayer/ows/persona/personaLinkingOperations';

import { refreshCurrentPersonaListData } from '../user/personaListActions';
import { getContactSourceName } from '../../utils/contactsStatistics';

export type { HubPerson };

export interface UnlinkNotificationActionContext {
    dispatch: Dispatch;
    linkedContact: LinkedContact;
}

export const unlinkContactCompletedAction = createDataActionFactory<
    { unlinkedContactSourceId: string; updatedPersona: HubPerson },
    { error: Error; unlinkedContactSourceId: string }
>('UnlinkPersonasCompletedAction', Feature.Linking);

export const linkPersonasCompletedAction = createDataActionFactory<
    { oldHubPersonaIds: string[]; linkedPersona: HubPerson },
    Error
>('LinkPersonasCompletedAction', Feature.Linking);
export const unlinkContactClickedAction = createActionFactory<{ contactSourceId: string }>(
    'UnlinkContactClickedAction',
    Feature.Linking
);
export const linkPersonasClickedAction = createActionFactory<{ count: number }>(
    'LinkPersonasClickedAction',
    Feature.Linking,
    action => ({
        count: action.payload.count,
    })
);
export const cacheClearedAfterLinkingAction = createDataActionFactory<void, void>(
    'CacheClearedAfterLinkingAction',
    Feature.Linking
);

const logSafePersonaIds = (personaIds: string[], adPersonaId: string) => ({
    adPersonaId: adPersonaId ? getLogSafeId(adPersonaId) + '_' + adPersonaId.length : null,
    personaIds: personaIds
        .map(personaId => getLogSafeId(personaId) + '_' + personaId.length)
        .join(','),
});

export const unlinkContact = (dispatch: ThunkDispatch) => (linkedContact: LinkedContact) => {
    dispatch(unlinkContactClickedAction({ contactSourceId: linkedContact.sourceId }));

    unlinkContactOperation(linkedContact).then(
        (updatedPersona: HubPerson) => {
            // The OWS UpdatePersona API sometimes returns a 200/OK without doing the actual unlinking. We want to log
            // this to better understand the scale of the problem. We therefore check if the returned persona still contains
            // the contact which was to be unlinked.
            if (
                updatedPersona.type === PersonType.Person &&
                updatedPersona.linkedContacts.some(
                    contact => contact.sourceId === linkedContact.sourceId
                )
            ) {
                getLogger().log('UnlinkPersona_OkButNoUnlink', {
                    source: getContactSourceName(linkedContact),
                });
            }

            dispatch(
                unlinkContactCompletedAction.success({
                    unlinkedContactSourceId: linkedContact.sourceId,
                    updatedPersona,
                })
            );
            invalidateCachedContacts(dispatch)([updatedPersona.personaId]);
            dispatch(refreshCurrentPersonaListData);
        },
        error => {
            dispatch(
                unlinkContactCompletedAction.error({
                    error: error,
                    unlinkedContactSourceId: linkedContact.sourceId,
                })
            );

            addNotification<UnlinkNotificationActionContext>(
                loc(somethingWentWrongLabel),
                loc(contactUnlinkingDialogErrorDescription),
                loc(cancelLabel)
            );
        }
    );
};

export const linkPersonas = (hubPersonas: HubPerson[]) =>
    createThunk('linkPersonas', (dispatch: ThunkDispatch, getState: () => {}) => {
        dispatch(linkPersonasClickedAction({ count: hubPersonas.length }));

        // If one of the contacts to link is from AD/GAL, it must be separated from the others
        const personaIds: string[] = [];
        let adPersonaId: string = null;

        hubPersonas.forEach(persona => {
            if (
                persona.type === PersonType.Person &&
                persona.info.GALContactGuid != null &&
                persona.linkedContacts.length === 1
            ) {
                adPersonaId = persona.personaId;
            } else {
                personaIds.push(persona.personaId);
            }
        });

        const hubPersonaIds = hubPersonas.map(persona => persona.hubPersonaId);
        const allFavorites = getFavorites(getAppState(getState()));
        const favoritesBeingLinked = hubPersonas
            .map(persona =>
                getMatchingFavoritePerson(
                    persona.hubPersonaId,
                    persona.info.mainEmailAddress,
                    allFavorites
                )
            )
            .filter(matchingFavorite => matchingFavorite);

        linkPersonasOperation(
            personaIds,
            adPersonaId,
            logSafePersonaIds(personaIds, adPersonaId)
        ).then(
            (linkedPersona: Person) => {
                dispatch(
                    linkPersonasCompletedAction.success({
                        oldHubPersonaIds: hubPersonaIds,
                        linkedPersona: linkedPersona,
                    })
                );
                invalidateCachedContacts(dispatch)(personaIds).then(
                    () => dispatch(cacheClearedAfterLinkingAction.success()),
                    () => dispatch(cacheClearedAfterLinkingAction.error())
                );
                dispatch(refreshCurrentPersonaListData);

                // If any of the personas were a favorite, unfavorite all of them and then favorite the result to ensure consistency
                if (favoritesBeingLinked.length) {
                    unfavoriteMultiplePersonas(favoritesBeingLinked, dispatch)
                        .then(() =>
                            dispatch(addFavoritePersonaClicked(linkedPersona, 'linkContacts'))
                        )
                        .catch(() => getLogger().log('FailureLinkingFavoritedContacts'));
                }
            },
            error => dispatch(linkPersonasCompletedAction.error(error))
        );
    });
export type { Action } from '../../../shared';
export type { DataActionFactory } from '../../../shared';
